// Auto-detection of recurring transactions.
//
// Two jobs:
//   1. detectRecurringCandidates(txs)   — surface untracked patterns to the user
//   2. matchExistingSeries(tx, series)  — at sync time, find which confirmed
//      recurring series a new transaction belongs to so we can auto-mark it
//
// Both use the shared recurring identity (description + type, amount-free)
// so that variable-amount bills (utilities, phone, price-changed subs) are
// recognized as one series instead of fragmenting per amount.

// Extension-qualified: the server-side sync pipeline imports this module
// directly, and Node's ESM resolver does not guess extensions the way the
// web bundler does.
import { seriesKeyOf, legacySeriesKeyOf } from './series-key.js';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Amounts within a series can vary, but if they vary WILDLY the "series" is
// probably unrelated transactions sharing a vague description (e.g. random
// "Transfer" amounts). Reject groups whose spread is implausible for a bill.
// Coefficient of variation = stddev / mean. ~0.3 covers normal utility swing;
// we allow up to 0.6 before treating the group as noise.
const MAX_AMOUNT_CV = 0.6;

// Share of a group's instances that must be transfer-paired before we trust
// the group enough to waive MAX_AMOUNT_CV. Not 1.0: detectTransfers works on a
// few-day window, so a payment posting late (or its other leg not yet synced)
// legitimately leaves one instance unpaired without changing what the series is.
const TRANSFER_PAIRED_RATIO = 0.6;

function amountStats(items) {
  const amounts = items.map((t) => Math.abs(Number(t.amount) || 0));
  const n = amounts.length;
  const mean = amounts.reduce((s, v) => s + v, 0) / n;
  const variance = amounts.reduce((s, v) => s + (v - mean) * (v - mean), 0) / n;
  const stddev = Math.sqrt(variance);
  const cv = mean > 0 ? stddev / mean : 0;
  const sorted = [...amounts].sort((a, b) => a - b);
  const median = sorted[Math.floor((sorted.length - 1) / 2)];
  return { mean, median, min: sorted[0], max: sorted[sorted.length - 1], cv };
}

const NOMINAL_CADENCES = [
  { type: 'daily',    days: 1,   tolerance: 0.5 },
  { type: 'weekly',   days: 7,   tolerance: 2 },
  { type: 'biweekly', days: 14,  tolerance: 3 },
  { type: 'monthly',  days: 30,  tolerance: 5 },
  { type: 'semiannual', days: 182, tolerance: 15 },
  { type: 'yearly',   days: 365, tolerance: 21 },
];

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }
  return new Date(value);
}

function classifyDelta(days) {
  let best = null;
  let bestErr = Infinity;
  for (const c of NOMINAL_CADENCES) {
    const err = Math.abs(days - c.days);
    if (err <= c.tolerance && err < bestErr) {
      best = c.type;
      bestErr = err;
    }
  }
  return best;
}

// Returns 'daily'|'weekly'|'biweekly'|'monthly'|'semiannual'|'yearly'|null based on
// vote among deltas. Requires >= 70% agreement to claim a cadence.
export function detectCadence(deltas) {
  if (!deltas || deltas.length === 0) return null;
  const votes = deltas.map(classifyDelta);
  const counts = {};
  votes.forEach((v) => { if (v) counts[v] = (counts[v] || 0) + 1; });
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  const [topType, topCount] = entries[0];
  if (topCount / deltas.length >= 0.7) return topType;
  return null;
}

// Find which confirmed recurring series (if any) a transaction belongs to.
// recurringSeries is the output of buildRecurringSeries() — each element has
// at least { key, desc, type, amt, recurrenceType }.
//
// Matches by series identity (desc+type), then sanity-checks the amount so a
// one-off $4,000 "Amazon" purchase doesn't get auto-attached to a recurring
// ~$15 "Amazon Prime" series. The band is generous (0.25×–4× the series'
// representative amount) — enough to allow real bill swing, tight enough to
// reject obviously-unrelated charges.
const AUTO_ATTACH_LOW = 0.25;
const AUTO_ATTACH_HIGH = 4;

export function matchExistingSeries(tx, recurringSeries) {
  if (!tx || tx.isRecurring) return null;
  const key = seriesKeyOf(tx);
  const match = recurringSeries.find((s) => s.key === key);
  if (!match) return null;
  const ref = Math.abs(Number(match.amt) || 0);
  const amt = Math.abs(Number(tx.amount) || 0);
  if (ref > 0 && (amt < ref * AUTO_ATTACH_LOW || amt > ref * AUTO_ATTACH_HIGH)) {
    return null;
  }
  return match;
}

// Main detector. Returns ranked list of candidates for user confirmation.
// options:
//   minInstances     (default 3)   — fewer doesn't constitute a pattern
//   dismissedKeys    (default {})  — series keys to skip
//   today            (default new Date()) — for "stale" recency filter
//   maxStalenessDays (default null) — drop candidates whose last instance is
//                                      older than 2× their cadence-nominal days
//   transferIds      (default empty) — ids that detectTransfers() paired to
//                                      another of the user's accounts; see
//                                      TRANSFER_PAIRED_RATIO below
export function detectRecurringCandidates(transactions, options = {}) {
  const {
    minInstances = 3,
    dismissedKeys = {},
    isDismissed,
    today = new Date(),
    transferIds = new Set(),
  } = options;
  const dismissed = (key) => {
    if (typeof isDismissed === 'function') return !!isDismissed(key);
    return !!dismissedKeys[key];
  };

  // Exclude any series where ANY instance is already marked recurring —
  // the user already handled it.
  const handledKeys = new Set();
  transactions.forEach((t) => {
    if (t.isRecurring) handledKeys.add(seriesKeyOf(t));
  });

  // Group unmarked transactions by series key.
  const groups = {};
  transactions.forEach((t) => {
    if (t.isRecurring) return;
    if (!t.description || !Number.isFinite(Number(t.amount))) return;
    const key = seriesKeyOf(t);
    if (handledKeys.has(key)) return;
    (groups[key] = groups[key] || []).push(t);
  });

  const candidates = [];
  Object.entries(groups).forEach(([key, items]) => {
    if (dismissed(key)) return;
    // Dismissals recorded before the key ignored reference numbers are stored
    // under the raw description, so a group that has since merged several of
    // those still has to stay dismissed rather than come back as a suggestion.
    if (items.some((t) => dismissed(legacySeriesKeyOf(t)))) return;
    if (items.length < minInstances) return;
    const sorted = [...items].sort((a, b) => parseDate(a.date) - parseDate(b.date));
    const dates = sorted.map((t) => parseDate(t.date)).filter(Boolean);
    if (dates.length < minInstances) return;

    const deltas = [];
    for (let i = 1; i < dates.length; i++) {
      deltas.push(Math.round((dates[i] - dates[i - 1]) / MS_PER_DAY));
    }

    const cadence = detectCadence(deltas);
    if (!cadence) return;

    // Amount cohesion — reject groups whose amounts are too scattered to be
    // a real bill (likely unrelated transactions sharing a description).
    //
    // Exception: credit-card payments. Paying a statement balance swings with
    // whatever you charged that month, so a real, perfectly regular autopay
    // routinely blows past MAX_AMOUNT_CV (250/1800/600/2400 -> cv 0.69) and
    // gets thrown out as noise. The filter's premise is that we can't tell a
    // bill from unrelated transactions sharing a description — but when
    // detectTransfers() has already matched these to the other leg on one of
    // the user's own accounts, we have positive structural evidence of what
    // they are, and the premise doesn't hold. Cadence and recency still apply.
    const amt = amountStats(items);
    const pairedRatio = items.filter((t) => t.id && transferIds.has(t.id)).length / items.length;
    if (amt.cv > MAX_AMOUNT_CV && pairedRatio < TRANSFER_PAIRED_RATIO) return;

    // Recency check — if the last instance is much older than the cadence,
    // it's probably a one-off pattern that already ended, not a live series.
    const nominalDays = NOMINAL_CADENCES.find((c) => c.type === cadence).days;
    const last = dates[dates.length - 1];
    const staleDays = (parseDate(today) - last) / MS_PER_DAY;
    if (staleDays > nominalDays * 2.5) return;

    const sample = sorted[0];
    const matched = deltas.map(classifyDelta).filter((d) => d === cadence).length;
    const regularity = matched / deltas.length;
    const confidence = Math.min(1, regularity * (Math.min(items.length, 6) / 6));
    const variable = amt.cv > 0.05;

    candidates.push({
      seriesKey: key,
      description: sample.description,
      // Representative amount = median (robust to a single outlier month).
      amount: parseFloat(amt.median.toFixed(2)),
      amountAvg: parseFloat(amt.mean.toFixed(2)),
      amountMin: parseFloat(amt.min.toFixed(2)),
      amountMax: parseFloat(amt.max.toFixed(2)),
      variable,
      type: sample.type,
      category: sample.category,
      recurrenceType: cadence,
      count: items.length,
      lastDate: last.toISOString().slice(0, 10),
      ids: sorted.map((t) => t.id).filter(Boolean),
      confidence,
    });
  });

  candidates.sort((a, b) => b.confidence - a.confidence || b.count - a.count);
  return candidates;
}

export const _internal = { classifyDelta, NOMINAL_CADENCES, seriesKeyOf, amountStats };
